/**
 * 価格情報の端数処理に関する共通ユーティリティ
 * システム全体で統一された価格の丸め処理を提供
 */

/**
 * 価格を小数点2桁に丸める（四捨五入）
 * @param price - 丸める前の価格
 * @returns 小数点2桁に丸められた価格
 */
export const roundPrice = (price: number): number => {
  return Math.round(price * 100) / 100;
};

/**
 * 税込価格から税抜価格を計算（小数点2桁）
 * @param grossPrice - 税込価格
 * @param vatRate - VAT率（%）
 * @returns 税抜価格（小数点2桁）
 */
export const calculateNetPriceRounded = (grossPrice: number, vatRate: number): number => {
  if (!grossPrice || grossPrice <= 0) return 0;
  const netPrice = grossPrice / (1 + vatRate / 100);
  return roundPrice(netPrice);
};

/**
 * パーセンテージ計算を行い小数点2桁に丸める
 * @param baseAmount - 基準金額
 * @param percentage - パーセンテージ（0.58 = 58%）
 * @returns 計算結果（小数点2桁）
 */
export const calculatePercentageRounded = (baseAmount: number, percentage: number): number => {
  const result = baseAmount * percentage;
  return roundPrice(result);
};

/**
 * 割引額を計算して小数点2桁に丸める
 * @param originalPrice - 元の価格
 * @param discountedPrice - 割引後の価格
 * @returns 割引額（小数点2桁）
 */
export const calculateDiscountAmountRounded = (originalPrice: number, discountedPrice: number): number => {
  const discountAmount = originalPrice - discountedPrice;
  return roundPrice(discountAmount);
};

/**
 * 割引率を計算して小数点2桁に丸める
 * @param originalPrice - 元の価格
 * @param discountAmount - 割引額
 * @returns 割引率（%、小数点2桁）
 */
export const calculateDiscountRateRounded = (originalPrice: number, discountAmount: number): number => {
  if (originalPrice <= 0) return 0;
  const rate = (discountAmount / originalPrice) * 100;
  return roundPrice(rate);
};

/**
 * 複数の価格を合計して小数点2桁に丸める
 * @param prices - 価格の配列
 * @returns 合計額（小数点2桁）
 */
export const sumPricesRounded = (prices: number[]): number => {
  const sum = prices.reduce((total, price) => total + price, 0);
  return roundPrice(sum);
};

/**
 * 価格と数量から小計を計算して小数点2桁に丸める
 * @param price - 単価
 * @param quantity - 数量
 * @returns 小計（小数点2桁）
 */
export const calculateSubtotalRounded = (price: number, quantity: number): number => {
  const subtotal = price * quantity;
  return roundPrice(subtotal);
};